import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Field from '../../../../../../../components/Form/Field'
import FieldLabelWrapper from '../../../../../../../features/components/Form/FieldLabelWrapper'
import ErrorMessage from '../../../../../../../components/Form/ErrorMessage'
import CostCapApproachQuestion from './CostCapApproachQuestion'
import ToggleField from '../../../../../../../components/Form/ToggleField'
import MissingDataWarnings from '../MissingDataWarnings'
import AppPromotionIos14Fields from '../AppPromotionIos14Fields'

import { useFormattedObjectivesList, useObjectivesListIsLoading } from '../hooks'
import { formatNumber } from '../../../../../../../helpers/numbers'
import { getShowBudgetSection } from '../../../../../../Multiplatform/CombinedFacebookTiktokCreate/CreateFormSelector/CampaignCreateForm/helper'

import { selectedFacebookAdAccountSelector } from '../../../../../../../modules/selectors/adAccounts'

import { BUDGET_LIFETIME } from '../../../../../../Multiplatform/CombinedFacebookTiktokCreate/CreateFormSelector/CampaignCreateForm/fields'
import { OBJECTIVE } from '../../../../../../Multiplatform/CampaignForms/fields'
import { OUTCOME_APP_PROMOTION, OUTCOME_SALES, OUTCOME_TRAFFIC } from '../../../../../../../constants/campaigns'

import { BIDDING_TYPE } from '../../../../../CampaignForms/CampaignFacebookCreate/CampaignFacebookCreateForm/fields'

import useDrawerFormStyles from '../../../../../../../styles/common/drawerForms'

import { AUTO_OPTIMIZED_CAMPAIGN_BUDGET } from '../../../../../../reusableFields'
import { fontWeightBold } from '../../../../../../../styles/typography'

const NewCampaignBudgetAllocationFields = ({ formik, remainingBudget }) => {
  const drawerFormClasses = useDrawerFormStyles()

  const { t } = useTranslation()

  const { currency_symbol: currencySymbol } = useSelector(selectedFacebookAdAccountSelector)

  const { values, setFieldValue } = formik

  const formattedObjectivesList = useFormattedObjectivesList()
  const isObjectivesListLoading = useObjectivesListIsLoading()

  const showBudgetSection = getShowBudgetSection(values[OBJECTIVE])
  const showCostCapSection = useMemo(
    () =>
      values[OBJECTIVE] === OUTCOME_SALES ||
      values[OBJECTIVE] === OUTCOME_TRAFFIC ||
      values[OBJECTIVE] === OUTCOME_APP_PROMOTION,
    [values]
  )

  if (remainingBudget < 1) {
    return <ErrorMessage fontSize={14} error="Your total remaining budget is too low to create a campaign." />
  }

  return (
    <>
      <div className={drawerFormClasses.subSection}>
        <FieldLabelWrapper label={t('Campaign goal')}>
          <p>{t('To upload the ad to a new campaign, please select the campaign optimisation goal.')}</p>
          <Field
            name={OBJECTIVE}
            label="Goal"
            placeholder="Select goal..."
            formik={formik}
            options={formattedObjectivesList}
            isLoading={isObjectivesListLoading}
          />
          <MissingDataWarnings values={values} />
        </FieldLabelWrapper>
      </div>
      {showBudgetSection && (
        <div className={drawerFormClasses.subSection}>
          <ToggleField
            name={AUTO_OPTIMIZED_CAMPAIGN_BUDGET}
            isEnabled={values[AUTO_OPTIMIZED_CAMPAIGN_BUDGET]}
            setFieldValue={setFieldValue}
            label={'Auto optimised campaign budget'}
            fontWeight={fontWeightBold}
            isSolid={false}
          />
          {values[AUTO_OPTIMIZED_CAMPAIGN_BUDGET] && (
            <>
              <p>
                {t(
                  'Please set a budget for this campaign. The budget will be shared across ad sets according to performance.'
                )}
              </p>
              <p>
                {t(`Your remaining budget is ${currencySymbol}` + formatNumber(remainingBudget, { max: 2, min: 2 }))}
              </p>
              <Field
                placeholder="Budget"
                type="number"
                formik={formik}
                name={BUDGET_LIFETIME}
                symbol={currencySymbol}
                inputMode="numeric"
              />
            </>
          )}
        </div>
      )}
      <AppPromotionIos14Fields formik={formik} />
      {showCostCapSection && (
        <CostCapApproachQuestion setFieldValue={setFieldValue} biddingType={values[BIDDING_TYPE]} />
      )}
    </>
  )
}

export default NewCampaignBudgetAllocationFields
